<template>
  <section class="faq">
    <div class="container">
      <div class="faq__header">
        <h2 class="faq__title font-inter">Questions we're frequently asked</h2>
        <p class="faq__subtitle font-inter">You might know them as FAQs</p>
      </div>
      
      <div class="faq__items">
        <div 
          v-for="(item, index) in faqItems" 
          :key="index"
          class="faq__item"
          :class="{ 'faq__item--expanded': expandedItems.has(index) }"
        >
          <button 
            class="faq__question font-inter"
            @click="toggleItem(index)"
            :aria-expanded="expandedItems.has(index)"
          >
            <span>{{ item.question }}</span>
            <span class="faq__icon">
              {{ expandedItems.has(index) ? '▲' : '▼' }}
            </span>
          </button>
          
          <div 
            v-if="expandedItems.has(index)"
            class="faq__answer font-inter"
            :aria-hidden="!expandedItems.has(index)"
          >
            <p>{{ item.answer }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'FAQ',
  setup() {
    const expandedItems = ref(new Set([0])) // First item expanded by default
    
    const faqItems = [
      {
        question: "What's the catch?",
        answer: "There's no catch. You refer the lead to us for the coverage you can't or don't want to quote. You keep the relationship and you get paid."
      },
      {
        question: "How do I know you won't steal my business?",
        answer: "Because that's bad business for us. We want to develop a long term relationship with all of our customers. Stealing your business is bad for our business. Plus we've had a bunch of lawyers write some very expensive documents which lay out our terms - we'd rather not spend lots of money on more lawyers."
      },
      {
        question: "What do I do with an insurance lead I can't quote?",
        answer: "Send it to us. We'll take it from there and when we sell the policy, you still get paid, and your customer is happy for the rounded service you've provided."
      },
      {
        question: "How much do I get paid?",
        answer: "You get a referral commission when we sell the policy. The amount depends on the carrier and premium, but the important part is you always know what you're getting."
      },
      {
        question: "When do I get paid?",
        answer: "As soon as the policy is bound and the carrier pays out. Usually that's 30–60 days."
      },
      {
        question: "Do I lose my client?",
        answer: "Nope. They're still your client. We only handle the coverage you send us, nothing else."
      },
      {
        question: "What types of leads can I send?",
        answer: "Right now we focus on workers' comp. Got other stuff you can't place? Send it anyway — if we can't help, we'll tell you."
      },
      {
        question: "Do I need to be licensed to send leads?",
        answer: "Yes, you need to hold a valid license in your state. You may want to send leads for lines that you're not licensed for or for out of state too, and that's something we can help with."
      },
      {
        question: "What if my lead doesn't buy?",
        answer: "Then nothing happens. You don't pay a dime, and you still keep your customer."
      },
      {
        question: "How do I know what's happening with my lead?",
        answer: "We keep you posted with a smart dashboard, showing the stage that each lead is at. You'll know where things stand, no guesswork."
      },
      {
        question: "Does it cost anything to join?",
        answer: "No. It's free. You only make money, never spend it."
      },
      {
        question: "Who are your carriers?",
        answer: "We work with a wide mix of national and regional carriers so we can place the tough stuff as well as the easy wins."
      },
      {
        question: "Can I send as many leads as I want?",
        answer: "Yes. One or one hundred - we'll take care of them. We may limit how many you can send us in a given period so we don't both get overwhelmed."
      },
      {
        question: "How do I know I'll get credit for my lead?",
        answer: "Every lead you send is tracked from the moment you hand it off. If we place the policy, it's tied back to you. No mix-ups, no missing commissions."
      }
    ]
    
    const toggleItem = (index) => {
      if (expandedItems.value.has(index)) {
        expandedItems.value.delete(index)
      } else {
        expandedItems.value.add(index)
      }
    }
    
    return {
      expandedItems,
      faqItems,
      toggleItem
    }
  }
}
</script>

<style scoped>
.faq {
  padding: 160px 0;
  background: white;
}

.faq__header {
  text-align: center;
  margin-bottom: 48px;
}

.faq__title {
  font-family: 'Fraunces', serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 1;
  color: #1e1e1e;
  letter-spacing: -1.2px;
  margin: 0 0 8px 0;
  font-variation-settings: 'SOFT' 0, 'WONK' 1;
}

.faq__subtitle {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 1.4;
  color: #757575;
  margin: 0;
  font-variation-settings: 'SOFT' 0, 'WONK' 1;
}

.faq__items {
  max-width: 800px;
  margin: 0 auto;
}

.faq__item {
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
}

.faq__question {
  width: 100%;
  padding: 24px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #1e1e1e;
  transition: background-color 0.2s ease;
}

.faq__question:hover {
  background-color: #f3f4f6;
}

.faq__question:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: -2px;
}

.faq__icon {
  color: var(--color-primary);
  font-size: var(--font-size-sm);
  transition: transform 0.2s ease;
}

.faq__item--expanded .faq__icon {
  transform: rotate(180deg);
}

.faq__answer {
  padding: 0 24px 24px;
  color: #757575;
  line-height: 1.4;
  animation: fadeIn 0.3s ease;
}

.faq__answer p {
  margin: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .faq {
    padding: var(--space-16) 0;
  }
  
  .faq__title {
    font-size: var(--font-size-2xl);
  }
  
  .faq__question {
    padding: var(--space-4);
    font-size: var(--font-size-base);
  }
  
  .faq__answer {
    padding: 0 var(--space-4) var(--space-4);
  }
}
</style>
